import {
  Children,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Stack, StackProps } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { Keys } from '@components/types/keyboard';
import { Color, Font, theme } from '@styles/theme';

export type CardListProps = HTMLAttributes<HTMLDivElement> & {
  header?: ReactElement<HeaderProps>;
  children: ReactNode;
};

export const CardList = observer<CardListProps, HTMLDivElement>(
  forwardRef((props, ref) => {
    const { header, children, ...rest } = props;

    return (
      <ListContainer ref={ref} {...rest}>
        {children}
      </ListContainer>
    );
  }),
);

type CardListItemProps = Partial<StackProps> &
  HTMLAttributes<HTMLDivElement> & {
    children: ReactNode;
  };

export const CardListItems = observer<CardListItemProps, HTMLDivElement>(
  forwardRef((props, ref) => {
    const { space = 2, children, ...rest } = props;
    const hasItems = Children.count(children) > 0;

    return (
      <ItemsContainer space={space} orientation="vertical" $empty={!hasItems} ref={ref} {...rest}>
        {children}
      </ItemsContainer>
    );
  }),
);

const ItemsContainer = styled(Stack)<{ $empty: boolean }>((p) => ({
  overflowY: 'auto',
  flexGrow: 1,
  height: '100%',
  padding: p.$empty ? 0 : '0 0.5rem 0.5rem 0.5rem',
  '> *': {
    border: 'none !important',
  },
}));

const ListContainer = styled.div({
  overflow: 'hidden',
  borderRadius: '0.25rem',
  background: theme.color.purpleTrans5,
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '100%',
});

type HeaderProps = {
  label: string;
  font?: Font;
  children?: ReactNode;
  accent?: Color;
  isDraggable?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const CardListHeader = observer<HeaderProps>((props) => {
  const { font = 'bold16', label, children, accent, isDraggable = false, ...rest } = props;

  return (
    <CardListHeaderContainer $accent={accent} $draggable={isDraggable}>
      <div {...rest} style={{ minWidth: 0 }}>
        <Text tag="div" font={font} text={label} ellipsis />
      </div>
      <div>{children}</div>
    </CardListHeaderContainer>
  );
});

const CardListHeaderContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
  align: 'flex-start',
})<{ $accent?: Color; $draggable: boolean }>((p) => ({
  flexShrink: 0,
  minHeight: '3.5rem',
  borderTop: '0.125rem solid ' + (p.$accent ? theme.color[p.$accent] : 'transparent'),
  '> div:first-child': {
    flexGrow: 1,
    height: '100%',
    padding: '1rem 0 1rem 1rem',
    ...(p.$draggable && {
      '&:hover': {
        cursor: 'grab',
      },
    }),
  },
  '> div:last-child': {
    display: 'flex',
    alignItems: 'center',
    paddingRight: '0.5rem',
    paddingTop: '0.375rem',
    gap: '0.5rem',
  },
}));

type EditHeaderProps = {
  defaultValue: string;
  onSubmit(newValue: string): void;
  onCancel(): void;
  cancelTooltip: string;
  submitTooltip: string;
};

export function CardListEditHeader(props: EditHeaderProps) {
  const { defaultValue, onSubmit, onCancel, cancelTooltip, submitTooltip } = props;
  const [value, setValue] = useState(defaultValue);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, []);

  function submit() {
    if (!value) {
      onCancel();
    } else {
      onSubmit(value);
    }
  }

  return (
    <EditHeaderContainer ref={ref}>
      <TextField
        autoFocus
        defaultValue={defaultValue}
        onChange={setValue}
        hideLabel
        label="Edit title"
        onKeyDown={(e) => {
          if (e.key === Keys.Enter) {
            submit();
          } else if (e.key === Keys.Escape) {
            onCancel();
          }
        }}
      />
      <Stack orientation="horizontal" space={2}>
        <Button
          variant="ghost"
          iconLeft={<IconCloseRegular color="red100" />}
          tooltip={cancelTooltip}
          onClick={onCancel}
        />
        <Button
          variant="ghost"
          data-cy="save-stage-button"
          iconLeft={<IconCheckmark color="green100" />}
          tooltip={submitTooltip}
          onClick={submit}
        />
      </Stack>
    </EditHeaderContainer>
  );
}

const EditHeaderContainer = styled.div({
  flexShrink: 0,
  height: '3.5rem',
  padding: '0 0.5rem',
  gap: '0.5rem',
  justifyContent: 'space-between',
  display: 'flex',
  alignItems: 'center',
  border: '0.125rem solid ' + theme.color.elementBlue100,
  svg: {
    opacity: '1 !important',
  },
});
